import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

export const api = axios.create({ baseURL: API_BASE });

// Attach JWT on every request
api.interceptors.request.use((cfg) => {
  const token = localStorage.getItem("db_token");
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  return cfg;
});

// Auto-logout on 401
api.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err?.response?.status === 401) {
      localStorage.removeItem("db_token");
      localStorage.removeItem("db_user");
      if (window.location.pathname.startsWith("/admin")) {
        window.location.href = "/login";
      }
    }
    return Promise.reject(err);
  }
);

// Convenience: format EUR
export const fmtEur = (n) =>
  new Intl.NumberFormat("en-IE", { style: "currency", currency: "EUR", minimumFractionDigits: 2 }).format(
    Number(n ?? 0)
  );

export const fileUrl = (storage_path) => `${API_BASE}/files/${storage_path}`;

/**
 * Normalise any error thrown from an api.* call into a human-readable string.
 * Handles:
 *  - FastAPI 422 validation errors: `detail` is Array<{type, loc, msg, ctx, ...}>
 *  - Regular HTTPException: `detail` is a string
 *  - Structured backend detail objects with `.message` / `.error`
 *  - Network / no-response errors
 */
export function getErrorMessage(err, fallback = "Something went wrong") {
  if (!err) return fallback;
  if (!err.response) {
    if (err.message === "Network Error") return "Network error — please check your connection.";
    return err.message || fallback;
  }
  const data = err.response.data;
  const detail = data?.detail;

  if (typeof detail === "string") return detail;

  if (Array.isArray(detail)) {
    const parts = detail
      .map((e) => {
        if (typeof e === "string") return e;
        if (e && typeof e === "object" && e.msg) {
          const field = Array.isArray(e.loc)
            ? e.loc.filter((x) => x !== "body" && typeof x !== "number").join(".")
            : "";
          return field ? `${field}: ${e.msg}` : e.msg;
        }
        try { return JSON.stringify(e); } catch { return String(e); }
      })
      .filter(Boolean);
    return parts.join("; ") || fallback;
  }

  if (detail && typeof detail === "object") {
    if (typeof detail.message === "string") return detail.message;
    if (typeof detail.error === "string") return detail.error;
    // Known structured detail objects (stock_conflicts, line_errors) — caller handles
    // them explicitly; here we just fall through to a generic message.
    return fallback;
  }

  if (typeof data?.message === "string") return data.message;
  return fallback;
}
